import { Button, Checkbox, Option, Radio, RadioGroup } from '@fluentui/react-components';
import { ArrowSyncFilled } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { getTooltipContentByFieldName } from '../../../common/tooltip/tooltip-get-content';
import { AccountMpnId, blankCountry, Organization } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput, FormInput, SelectOptions } from '../../shared-components/form';
import { PhoneNumberField, PhoneNumberInput } from '../../shared-components/phone-number-input';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import './CompanyInformation.scss';

interface CompanyInformationProps {
  accounts: AccountMpnId[];
  organization: Organization;
  displayMpnIdSelectionPreferance: boolean;
  validationErrors: Partial<Record<keyof Organization, string[]>>;
  displayRefreshFromPartnerCenter: boolean;
  displayOrganizationContactForm: boolean;
  jointNomineeRowKey?: string;
  onGetCompanyInformation?: (accountId: string) => Promise<void>;
  onSelectMpnIdInputOption?: (showMpnIdSelection: boolean) => void;
}

type MPNInputType = 'DROPDOWN' | 'TEXT';

const CompanyInformationImpl: React.FC<CompanyInformationProps> = (props) => {
  const {
    accounts,
    organization,
    displayMpnIdSelectionPreferance,
    validationErrors,
    displayRefreshFromPartnerCenter,
    displayOrganizationContactForm,
    jointNomineeRowKey,
    onGetCompanyInformation,
    onSelectMpnIdInputOption,
  } = props;

  const {
    userStore,
    countryStore,
    nominationWizardPageUiStore,
  } = useStores();

  const isBeingValidated = nominationWizardPageUiStore.isOrganizationBeingValidated(
    organization.organizationTypeId,
  );
  const isCeoEmailIncluded = jointNomineeRowKey
    ? nominationWizardPageUiStore.jointNomineeCeoEmailIncludedMap.get(jointNomineeRowKey) || false
    : nominationWizardPageUiStore.isCeoEmailIncluded;

  const handleCeoEmailIncludedChange = (isIncluded: boolean): void => {
    if (jointNomineeRowKey) {
      nominationWizardPageUiStore.setJointNomineeCeoEmailIncluded(jointNomineeRowKey, isIncluded);
      return;
    }

    nominationWizardPageUiStore.setCeoEmailIncluded(isIncluded);
  };

  const [mpnInputType, setMpnInputType] = useState<MPNInputType>(displayMpnIdSelectionPreferance ? 'DROPDOWN' : 'TEXT');
  const [selectedMpnId, setSelectedMpnId] = useState<string>(organization.mpnId);
  if ((mpnInputType === 'DROPDOWN') && onSelectMpnIdInputOption) {
    onSelectMpnIdInputOption(true);
  }

  const accountsSortedByMpnId =
    [...accounts]
      .sort((account1, account2) => account1.mpnId.localeCompare(account2.mpnId))
      .filter((account, index, accounts) => accounts.findIndex(a => a.mpnId === account.mpnId) === index);

  const getCompanyInformation = async (mpnId: string): Promise<void> => {
    const selectedAccount = accounts.find(account => account.mpnId === mpnId);
    if (!selectedAccount || !onGetCompanyInformation) return;

    await onGetCompanyInformation(selectedAccount.accountId);
    if (userStore.companyInformation == undefined) {
      organization.companyName = '';
      organization.address1 = '';
      organization.address2 = '';
      organization.city = '';
      organization.stateProvince = '';
      organization.postalCode = '';
      organization.country = blankCountry();
      return;
    }

    organization.mpnId = mpnId;
    organization.companyName = userStore.companyInformation.companyName;
    organization.address1 = userStore.companyInformation.address.addressLine1;
    organization.address2 = userStore.companyInformation.address.addressLine2;
    organization.city = userStore.companyInformation.address.city;
    organization.stateProvince = userStore.companyInformation.address.state;
    organization.postalCode = userStore.companyInformation.address.postalCode;

    const accountCountry = countryStore.awardCountries.find(country => country.countryCode === userStore.companyInformation?.address.country);
    organization.country = accountCountry ? accountCountry : blankCountry();
  };

  const renderMpnRadioButtons = (): React.JSX.Element => {
    return (
      <RadioGroup
        value={mpnInputType}
        onChange={(_, data) => {
          const type = data.value as MPNInputType;
          setMpnInputType(type);
          if (!onSelectMpnIdInputOption) return;
          onSelectMpnIdInputOption(type === 'DROPDOWN' ? true : false);
        }}
      >
        <Radio value='DROPDOWN' label='Use a Partner ID associated with my account' />
        <Radio value='TEXT' label='I do not see the Partner ID I wish to nominate for' />
      </RadioGroup>
    );
  };

  const renderOrganizationContactForm = (): React.JSX.Element => {
    return (
      <>
        <FlexBox
          wrap
          isFullWidth
          aligns='start'
          className='company-information__row'
        >
          <FlexBox
            isFullWidth
            aligns='start'
            className='row__sub-row'
          >
            <SelectOptions
              className='field--prefix'
              validationMessage={
                isBeingValidated && validationErrors.prefix?.length
                  ? <ValidationMessageList messages={validationErrors.prefix} />
                  : undefined
              }
              label='Prefix'
              onOptionSelect={(optionData) => organization.prefix = optionData.optionValue ? optionData.optionValue : ''}
              dropdownProps={{
                inlinePopup: true,
                value: organization.prefix.trim(),
                selectedOptions: [organization.prefix.trim()],
              }}
            >
              <Option value='' text='' />
              <Option value='Mr.'>Mr.</Option>
              <Option value='Mrs.'>Mrs.</Option>
              <Option value='Ms.'>Ms.</Option>
            </SelectOptions>

            <FormInput
              required
              label='First name'
              validationMessage={
                isBeingValidated && validationErrors.firstName?.length
                  ? <ValidationMessageList messages={validationErrors.firstName} />
                  : undefined
              }
              onChange={(firstName) => organization.firstName = firstName}
              inputProps={{
                type: 'text',
                maxLength: 250,
                value: organization.firstName,
              }}
            />
          </FlexBox>
          <FormInput
            required
            label='Last name'
            validationMessage={
              isBeingValidated && validationErrors.lastName?.length
                ? <ValidationMessageList messages={validationErrors.lastName} />
                : undefined
            }
            onChange={(lastName) => organization.lastName = lastName}
            inputProps={{
              type: 'text',
              maxLength: 250,
              value: organization.lastName,
            }}
          />
        </FlexBox>
        <FormInput
          required
          label='Contact email'
          tooltipText={getTooltipContentByFieldName('joint-nominee', 'contactEmail')}
          validationMessage={
            isBeingValidated && validationErrors.emailAddress?.length
              ? <ValidationMessageList messages={validationErrors.emailAddress} />
              : undefined
          }
          onChange={(emailAddress) => organization.emailAddress = emailAddress}
          inputProps={{
            type: 'text',
            maxLength: 250,
            value: organization.emailAddress,
          }}
        />
      </>
    );
  };

  return (
    <FlexBox
      direction='column'
      className='company-information'
    >
      <FlexBox
        isFullWidth
        wrap
        content='space-between'
        aligns='start'
        className='company-information__row'
      >
        {displayMpnIdSelectionPreferance && renderMpnRadioButtons()}
        {
          displayRefreshFromPartnerCenter && mpnInputType === 'DROPDOWN' && (
            <Button
              appearance='secondary'
              icon={<ArrowSyncFilled color='#0078D4' />}
              onClick={() => getCompanyInformation(selectedMpnId)}
              disabled={!selectedMpnId || selectedMpnId.length === 0}
            >
              Refresh from partner center
            </Button>
          )
        }
      </FlexBox>
      <FlexBox
        isFullWidth
        aligns='start'
        className='company-information__row'
      >
        {
          mpnInputType === 'DROPDOWN' && accountsSortedByMpnId.length ? (
            <SelectOptions
              required
              label='Partner ID'
              validationMessage={
                isBeingValidated && validationErrors.mpnId?.length
                  ? <ValidationMessageList messages={validationErrors.mpnId} />
                  : undefined
              }
              onOptionSelect={(optionData) => {
                if (!optionData.optionValue) return;
                setSelectedMpnId(optionData.optionValue);
                getCompanyInformation(optionData.optionValue);
              }}
              dropdownProps={{
                inlinePopup: true,
                value: organization.mpnId,
                selectedOptions: [organization.mpnId],
              }}
            >
              {
                accountsSortedByMpnId
                  .map((account, accountIndex) => (
                    <Option
                      key={accountIndex}
                      value={account.mpnId}
                    >
                      {account.mpnId}
                    </Option>
                  ))
              }
            </SelectOptions>
          ) :
            <FormInput
              required
              label='Partner ID'
              validationMessage={
                isBeingValidated && validationErrors.mpnId?.length
                  ? <ValidationMessageList messages={validationErrors.mpnId} />
                  : undefined
              }
              onChange={(mpnId) => organization.mpnId = mpnId}
              inputProps={{
                required: true,
                type: 'text',
                value: organization.mpnId,
                maxLength: 50,
              }}
            />
        }
        <FormInput
          required
          label='Company'
          validationMessage={
            isBeingValidated && validationErrors.companyName?.length
              ? <ValidationMessageList messages={validationErrors.companyName} />
              : undefined
          }
          onChange={(companyName) => organization.companyName = companyName}
          inputProps={{
            required: true,
            type: 'text',
            value: organization.companyName,
            maxLength: 100,
          }}
        />
      </FlexBox>

      <FlexBox
        isFullWidth
        aligns='start'
        className='company-information__row'
      >
        <FormInput
          required
          label='Address'
          validationMessage={
            isBeingValidated && validationErrors.address1?.length
              ? <ValidationMessageList messages={validationErrors.address1} />
              : undefined
          }
          onChange={(address1) => organization.address1 = address1}
          inputProps={{
            type: 'text',
            value: organization.address1,
          }}
        />
        <FormInput
          label='Address 2'
          validationMessage={
            isBeingValidated && validationErrors.address2?.length
              ? <ValidationMessageList messages={validationErrors.address2} />
              : undefined
          }
          onChange={(address2) => organization.address2 = address2}
          inputProps={{
            type: 'text',
            value: organization.address2,
          }}
        />
      </FlexBox>

      <FlexBox
        wrap
        isFullWidth
        aligns='start'
        className='company-information__row'
      >
        <FlexBox
          isFullWidth
          aligns='start'
          className='row__sub-row'
        >
          <FormInput
            required
            label='City'
            validationMessage={
              isBeingValidated && validationErrors.city?.length
                ? <ValidationMessageList messages={validationErrors.city} />
                : undefined
            }
            onChange={(city) => organization.city = city}
            inputProps={{
              type: 'text',
              value: organization.city,
              maxLength: 50,
            }}
          />
          <FormInput
            label='State / Province'
            validationMessage={
              isBeingValidated && validationErrors.stateProvince?.length
                ? <ValidationMessageList messages={validationErrors.stateProvince} />
                : undefined
            }
            onChange={(stateProvince) => organization.stateProvince = stateProvince}
            inputProps={{
              type: 'text',
              value: organization.stateProvince,
              maxLength: 50,
            }}
          />
        </FlexBox>

        <FlexBox
          isFullWidth
          aligns='start'
          className='row__sub-row'
        >
          <FormInput
            label='Zip code'
            tooltipText={getTooltipContentByFieldName('company-information', 'postalCode')}
            validationMessage={
              isBeingValidated && validationErrors.postalCode?.length
                ? <ValidationMessageList messages={validationErrors.postalCode} />
                : undefined
            }
            onChange={(postalCode) => organization.postalCode = postalCode}
            inputProps={{
              type: 'text',
              value: organization.postalCode,
              maxLength: 50,
            }}
          />
          <SelectOptions
            required
            label='Country'
            validationMessage={
              isBeingValidated && validationErrors.country?.length
                ? <ValidationMessageList messages={validationErrors.country} />
                : undefined
            }
            onOptionSelect={(optionData) => {
              if (!optionData.optionValue) return;
              const country = countryStore.awardCountries.find(country => country.countryCode === optionData.optionValue);
              if (!country) return;
              organization.country = { ...country };
            }}
            dropdownProps={{
              inlinePopup: true,
              value: organization.country.displayName,
              selectedOptions: [organization.country.countryCode],
            }}
          >
            {
              countryStore.awardCountries.map((country) => (
                <Option
                  key={country.countryCode}
                  value={country.countryCode}
                >
                  {country.displayName}
                </Option>
              ))
            }
          </SelectOptions>
        </FlexBox>
      </FlexBox>

      <FlexBox
        wrap
        aligns='start'
        isFullWidth
        className='company-information__row'
      >
        <FormattedInput
          prefix={'https://'}
          label='Website URL *'
          value={organization.websiteUrl}
          onChange={websiteUrl => organization.websiteUrl = websiteUrl}
          errorText={
            isBeingValidated && validationErrors.websiteUrl?.length
              ? <ValidationMessageList messages={validationErrors.websiteUrl} />
              : undefined
          }
          maxLength={500}
          isValidated={isBeingValidated}
          required
        />
        <PhoneNumberInput
          id={PhoneNumberField.Organization}
          value={organization.phoneNumber}
          onChange={(phoneNumber) => {
            organization.phoneNumber = phoneNumber
          }}
          onCountryChange={(id, newCountryCode) => {
            nominationWizardPageUiStore.setSelectedCountryCode(id, newCountryCode);
          }}
          error={
            isBeingValidated && validationErrors.phoneNumber?.length
              ? <ValidationMessageList messages={validationErrors.phoneNumber} />
              : undefined
          }
          label='Company phone number'
          required
        />
      </FlexBox>

      <FlexBox
        direction='column'
      >
        <FormInput
          label='Regional CEO full name'
          validationMessage={
            isBeingValidated && validationErrors.regionalCeoFullName?.length
              ? <ValidationMessageList messages={validationErrors.regionalCeoFullName} />
              : undefined
          }
          onChange={(regionalCeoFullName) => organization.regionalCeoFullName = regionalCeoFullName || undefined}
          inputProps={{
            type: 'text',
            value: organization.regionalCeoFullName || '',
            maxLength: 50,
          }}
        />
        <Checkbox
          checked={isCeoEmailIncluded}
          label='If selected as a winner or finalist, include CEO on notification email'
          onChange={(_, data) => handleCeoEmailIncludedChange(data.checked === true)}
        />
        {
          isCeoEmailIncluded && (
            <FormInput
              required
              label='Regional CEO Email'
              validationMessage={
                isBeingValidated && validationErrors.regionalCeoEmail?.length
                  ? <ValidationMessageList messages={validationErrors.regionalCeoEmail} />
                  : undefined
              }
              onChange={(regionalCeoEmail) => organization.regionalCeoEmail = regionalCeoEmail || undefined}
              inputProps={{
                type: 'text',
                value: organization.regionalCeoEmail || '',
                maxLength: 250,
              }}
            />
          )
        }
      </FlexBox>
      {displayOrganizationContactForm && renderOrganizationContactForm()}
    </FlexBox>
  );
};

export const CompanyInformation = observer(CompanyInformationImpl);
